<template>
<component
	:is="resolvedComponent"
	class="focus-visible:outline-info-200 inline-flex items-center justify-center whitespace-nowrap rounded-full border-current font-bold transition-colors disabled:text-neutral-400"
	:class="classes"
	:disabled="disabled || loading"
	:to="external ? undefined : to"
	:href="href || (external ? to : undefined)"
	:target="target"
	:external="external"
	:type="resolvedComponent === 'button' ? type : undefined"
	:aria-label="ariaLabel || (iconOnly ? icon : undefined)"
>
	<OIcon
		v-if="icon && !loading && iconPos === 'left'"
		:name="icon"
		:size="iconSize ?? sizeConfig.iconSize"
		:class="iconClasses"
		:set="iconSet"
		:aria-hidden="!iconOnly"
	/>
	<Transition name="button-loading">
		<OLoader v-if="loading" class="-ml-2 mr-2" hideLabel type="ring"/>
	</Transition>
	<slot name="label">
		{{ label }}
	</slot>
	<OIcon
		v-if="icon && iconPos === 'right'"
		:name="icon"
		:size="iconSize ?? sizeConfig.iconSize"
		:class="iconClasses"
		:set="iconSet"
		:aria-hidden="!iconOnly"
	/>
</component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import OIcon from '../OIcon.vue';
import OLoader from '../OLoader.vue';
import {
	colors,
	sizes,
	type ButtonColor,
	type ButtonSize,
	type ButtonVariant,
	widthClassMap,
} from './buttonConfig';

interface OButtonProps {
	/** Tekst przycisku */
	label?: string;
	/** Nazwa ikony, przekazywana do OIcon */
	icon?: string;
	/** Nazwa setu ikon, przekazywana do OIcon
	 * @default 'base'
	 */
	iconSet?: string;
	/** Pozycja ikony
	 * @default 'left'
	 * @values 'left', 'right'
	 */
	iconPos?: 'left' | 'right';
	/** Wariant przycisku
	 * @default 'filled'
	 * @values 'filled', 'outlined', 'ghost', 'inverted'
	 *
	 * - filled - klasyczny z wypełnionym tłem
	 * - outlined - z obramowaniem i bez tła
	 * - ghost - sam tekst, bez tła i obramowania
	 * - inverted - sam tekst na białym tle
	 */
	variant?: ButtonVariant;
	/** Kolor przycisku
	 * @default 'accent'
	 * @values 'accent', 'info', 'success', 'danger' 'neutral'
	 */
	color?: ButtonColor;
	/** Rozmiar przycisku
	 * @default 'md'
	 * @values 'sm', 'md', 'lg'
	 */
	size?: ButtonSize;
	/** Czy przycisk ma mieć pełną szerokość */
	fullWidth?: boolean;
	/** Breakpoint, do którego przycisk ma mieć pełną szerokość */
	fullWidthUntil?: keyof typeof widthClassMap;
	/** Czy przycisk znajduje się w stanie ładowania.
	 * Dodaje loader i ustawia przycisk na disabled */
	loading?: boolean;
	/** Wyłącza przycisk */
	disabled?: boolean;
	/** Ścieżka z vue-routera. Jeśli jest podana, to button jest renderowany jako RouterLink */
	to?: RouteLocationRaw;
	/** Atrybut target dla linków */
	target?: string;
	/** Czy link jest zewnętrzny */
	external?: boolean;
	/** Typ przycisku
	 * @default 'button'
	 */
	type?: string;
	/** Href dla linków. Jeśli jest podany, to przycisk jest renderowany jako tag 'a' */
	href?: string;
	/** Rozmiar ikony, przekazywany do OIcon */
	iconSize?: number;
	/** ARIA label for accessibility */
	ariaLabel?: string;
};

const props = withDefaults(defineProps<OButtonProps>(), {
	label: '',
	iconSet: 'base',
	iconPos: 'left',
	variant: 'filled',
	color: 'accent',
	size: 'md',
	to: undefined,
	target: undefined,
	external: undefined,
	type: 'button',
	href: undefined,
	iconSize: undefined,
	ariaLabel: undefined,
});

const resolvedComponent = computed(() => {
	if (props.href || (props.to && props.external)) {
		return 'a';
	}

	if (props.to) {
		return RouterLink;
	}

	return 'button';
});

const bindings = computed(() => {
	const base = {
		disabled: props.disabled || props.loading,
		to: props.external ? undefined : props.to,
		href: props.href || (props.external ? props.to : undefined),
		target: props.target,
		external: props.external,
		type: resolvedComponent.value === 'button' ? props.type : undefined,
	};

	// filter out undefined values
	return Object.fromEntries(Object.entries(base).filter(([, v]) => v !== undefined));
});



const iconOnly = computed(() => props.icon && !props.label);
const sizeConfig = computed(() => sizes[props.size][iconOnly.value ? 'iconOnly' : 'normal']);
const classes = computed(() => {
	const res = [sizeConfig.value.classes, colors[props.color][props.variant]];

	if (props.variant === 'filled' || props.variant === 'inverted') {
		res.push('disabled:bg-neutral-50 enabled:active:shadow-inner');
	}

	if (props.fullWidth) {
		res.push('w-full');
	} else if (props.fullWidthUntil) {
		res.push('w-full', widthClassMap[props.fullWidthUntil]);
	} else if (!iconOnly.value) {
		res.push('w-fit');
	}

	return res;
});

const iconClasses = computed(() => {
	if (iconOnly.value) {
		return '';
	}

	return sizes[props.size].icon[props.iconPos];
});
</script>

<style scoped lang="scss">
.button-loading-enter-active,
.button-loading-leave-active {
	@apply origin-left overflow-hidden transition-all;
}

.button-loading-enter-from,
.button-loading-leave-to {
	@apply mr-1 w-0 opacity-0;
}

.button-loading-enter-to,
.button-loading-leave-from {
	@apply w-[1em] opacity-100;
}
</style>
